import React, { useEffect, useRef, useState } from 'react';

const Mentorship = () => {
  const [visible, setVisible] = useState({});
  const refs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible((prev) => ({ ...prev, [entry.target.dataset.id]: true }));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(refs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addRef = (id) => (el) => {
    refs.current[id] = el;
    if (el) el.dataset.id = id;
  };

  const features = [
    {
      icon: '🎯',
      title: '5 Mock Interviews',
      desc: 'Structured personal interview simulations designed to mirror real finance recruiting — with detailed written feedback after each session.',
    },
    {
      icon: '📄',
      title: 'Resume Building',
      desc: 'Role-aligned CV review, deep structural edit, and final polish so your resume positions you precisely for the firms you\'re targeting.',
    },
    {
      icon: '💬',
      title: '24×7 WhatsApp Support',
      desc: 'Direct access to your mentor on personal WhatsApp — for quick questions, gut-check moments, and ongoing guidance throughout.',
    },
    {
      icon: '🧭',
      title: 'Career & Role Clarity',
      desc: 'In-depth conversations to map your profile to the right roles, firms, and timelines — so every application is deliberate, not random.',
    },
    {
      icon: '🏢',
      title: 'Firm Application Guidance',
      desc: 'Real-time support when companies are floated — role-specific prep, firm research, and application strategy tailored to the opportunity.',
    },
    {
      icon: '📚',
      title: 'Interview Materials',
      desc: 'Curated resources covering technical concepts, market awareness, and behavioral frameworks — exactly what gets asked in finance interviews.',
    },
    {
      icon: '✅',
      title: 'Placement Submission Review',
      desc: 'Every shortlisting form, SOP, or written submission reviewed and refined before you hit send — no blind shots.',
    },
  ];

  return (
    <div className="mentorship-page">

      {/* ── HERO ── */}
      <section className="hero-section">
        <div className="hero-bg-pattern" />
        <div className="container hero-inner">
          <div className="hero-badge">1-on-1 Personal Mentorship</div>
          <h1 className="hero-title">
            Your mentor. Your prep.<br />
            <span className="hero-accent">Until you're placed.</span>
          </h1>
          <p className="hero-sub">
            Not a course. Not a platform. Personal mentorship — from the day you enroll
            to the day you get placed. Your questions, your opportunities, your prep.
            I'm with you for all of it.
          </p>
          <div className="hero-cta-group">
            <a
              href="https://pages.razorpay.com/pl_RxYpGKIFTyqm4e/view"
              className="btn-primary-hero"
              target="_blank"
              rel="noreferrer"
            >
              Enroll Now — ₹7,999
            </a>
            <a href="#how-it-works" className="btn-ghost-hero">See how it works ↓</a>
          </div>
        </div>
        <div className="hero-scroll-line" />
      </section>

      {/* ── PROGRAM CARD ── */}
      <section className="section-gap" ref={addRef('plan')}>
        <div className="container">
          <div className={`section-label ${visible['plan'] ? 'fade-up' : 'pre-fade'}`}>
            The Program
          </div>
          <h2 className={`section-heading ${visible['plan'] ? 'fade-up delay-1' : 'pre-fade'}`}>
            Personal Mentorship
          </h2>
          <p className={`section-sub ${visible['plan'] ? 'fade-up delay-2' : 'pre-fade'}`}>
            One plan. End-to-end. Until you're placed.
          </p>

          {/* Honest disclaimer banner */}
          <div className={`disclaimer-banner ${visible['plan'] ? 'fade-up delay-2' : 'pre-fade'}`}>
            <span className="disclaimer-icon">📌</span>
            <p>
              <strong>Important — read before enrolling:</strong> This is a mentorship program, not a placement guarantee.
              We do not source or provide job opportunities. What we do is make sure you are fully prepared
              to convert every opportunity that comes your way — through your college, network, or recruiting season.
              You bring the shot. We make sure you don't miss it.
            </p>
          </div>

          <div className={`plan-card ${visible['plan'] ? 'fade-up delay-3' : 'pre-fade'}`}>
            {/* Left panel */}
            <div className="plan-left">
              <div className="plan-tag">Personal Mentorship</div>
              <div className="plan-price-block">
                <span className="plan-price">₹7,999</span>
                <span className="plan-price-note">until placed</span>
              </div>
              <p className="plan-pitch">
                Access doesn't expire after 30 days or 3 months.
                It ends when you get placed. That's the commitment.
              </p>
              <div className="plan-commitment">
                <div className="commitment-line">
                  <span className="commitment-check">✓</span>
                  <span>Dedicated mentor support — from day one to offer letter</span>
                </div>
                <div className="commitment-line">
                  <span className="commitment-check">✓</span>
                  <span>Every question answered. Every opportunity prepped for.</span>
                </div>
                <div className="commitment-line">
                  <span className="commitment-check">✓</span>
                  <span>Not a placement program — a preparation one.</span>
                </div>
              </div>
              <a
                href="https://pages.razorpay.com/pl_RxYpGKIFTyqm4e/view"
                className="btn-plan"
                target="_blank"
                rel="noreferrer"
              >
                Enroll Now
              </a>
              <p className="plan-note">Limited slots per cohort</p>
            </div>

            {/* Right panel – features grid */}
            <div className="plan-features">
              {features.map((f, i) => (
                <div className="feature-item" key={i}>
                  <div className="feature-icon">{f.icon}</div>
                  <div>
                    <div className="feature-title">{f.title}</div>
                    <div className="feature-desc">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ROI / Worth It ── */}
      <section className="section-gap roi-section" ref={addRef('roi')}>
        <div className="container">
          <div className={`roi-card ${visible['roi'] ? 'fade-up' : 'pre-fade'}`}>
            <div className="roi-left">
              <div className="roi-eyebrow">The honest math</div>
              <h2 className="roi-heading">
                ₹7,999 might feel like a lot.<br />It isn't.
              </h2>
              <p className="roi-body">
                Think about it this way. The difference between a ₹12 LPA role and a ₹20 LPA role
                is not just ₹8 lakhs a year. It's your first designation. Your next employer's
                benchmark. Your trajectory for the next decade.
              </p>
              <p className="roi-body">
                That gap doesn't come from working harder in the job — it comes from how
                prepared you were the day you walked into that interview.
              </p>
              <p className="roi-body" style={{ fontWeight: 600, color: 'var(--primary, #1a2332)' }}>
                ₹7,999 is not the cost of this program. It's the cost of not leaving that
                opportunity on the table.
              </p>
            </div>
            <div className="roi-right">
              <div className="roi-compare">
                <div className="roi-col roi-col-low">
                  <div className="roi-col-label">Without preparation</div>
                  <div className="roi-col-amount">₹12 LPA</div>
                  <div className="roi-col-note">Underprepped. Undersold. Underplaced.</div>
                  <ul className="roi-list">
                    <li>Generic answers in interviews</li>
                    <li>Resume not role-aligned</li>
                    <li>Missed shortlisting windows</li>
                    <li>No one to course-correct you</li>
                  </ul>
                </div>
                <div className="roi-col roi-col-high">
                  <div className="roi-col-badge">With mentorship</div>
                  <div className="roi-col-amount roi-col-amount-high">₹20 LPA</div>
                  <div className="roi-col-note">Sharper. Clearer. More confident.</div>
                  <ul className="roi-list">
                    <li>Crisp, firm-specific interview answers</li>
                    <li>Resume that gets you shortlisted</li>
                    <li>Every submission reviewed</li>
                    <li>Mentor in your corner at every step</li>
                  </ul>
                  <div className="roi-diff">+₹8 LPA difference</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── YOUR MENTOR ── */}
      <section className="section-gap mentor-section" ref={addRef('mentor')}>
        <div className="container">
          <div className={`section-label ${visible['mentor'] ? 'fade-up' : 'pre-fade'}`}>
            Your Mentor
          </div>
          <h2 className={`section-heading ${visible['mentor'] ? 'fade-up delay-1' : 'pre-fade'}`}>
             Aniket 
          </h2>

          <div className={`mentor-card ${visible['mentor'] ? 'fade-up delay-2' : 'pre-fade'}`}>
            <div className="mentor-photo-col">
              <div className="mentor-photo-frame">
                <img src="/aniket.jpg" alt=" Aniket " className="mentor-photo" />
              </div>
              <a
                href="https://www.linkedin.com/in/aniket-vaslas-9bb470155"
                target="_blank"
                rel="noreferrer"
                className="mentor-linkedin"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn Profile
              </a>
              <div className="mentor-tags-col">
                <span className="mentor-tag">Private Credit</span>
                <span className="mentor-tag">Alt. Investments</span>
                <span className="mentor-tag">IIM Ranchi</span>
                <span className="mentor-tag">Ex-JPMC Markets</span>
              </div>
            </div>

            <div className="mentor-bio">
              <div className="mentor-quote">
                "I don't give generic advice. I give you what I would want to hear."
              </div>
              <p>
                Aniket is an IIM Ranchi graduate currently working with one of the world's
                largest alternative asset managers in the private credit domain. His career spans
                institutional markets at JP Morgan, credit analysis, and real-world investment decisions.
              </p>
              <p>
                Over four years, he has mentored and interviewed hundreds of candidates across
                finance roles — from equity research to credit and quant. Student teams he trained
                have gone on to win equity research competitions and convert highly competitive placements.
              </p>
              <p>
                What sets Aniket apart is his deliberate, personalised approach. He takes the time
                to understand your background, thinking style, and constraints — and tailors guidance
                accordingly. Students consistently describe him as available, direct, and genuinely
                invested in their outcomes.
              </p>

              <div className="mentor-stats">
                <div className="stat">
                  <div className="stat-number">4+</div>
                  <div className="stat-label">Years mentoring</div>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                  <div className="stat-number">100s</div>
                  <div className="stat-label">Students mentored</div>
                </div>
                <div className="stat-divider" />
                <div className="stat">
                  <div className="stat-number">24×7</div>
                  <div className="stat-label">Personal access</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="section-gap hiw-section" id="how-it-works" ref={addRef('hiw')}>
        <div className="container">
          <div className={`section-label ${visible['hiw'] ? 'fade-up' : 'pre-fade'}`}>
            Process
          </div>
          <h2 className={`section-heading ${visible['hiw'] ? 'fade-up delay-1' : 'pre-fade'}`}>
            How It Works
          </h2>

          <div className="hiw-steps">
            {[
              {
                n: '01',
                title: 'Enroll',
                desc: 'Choose the program and complete enrollment. You\'ll receive a confirmation and onboarding details within 24 hours.',
              },
              {
                n: '02',
                title: 'Introductory Call',
                desc: 'A detailed 1-on-1 to map your background, gaps, target roles and firms — and build your personalised roadmap.',
              },
              {
                n: '03',
                title: 'Execution Phase',
                desc: 'Mock interviews, resume polish, materials, real-time WhatsApp support, submission reviews, and firm-specific prep as opportunities arise.',
              },
              {
                n: '04',
                title: 'Final Push',
                desc: 'Interview-ready profile, targeted applications, and continued guidance through your placement process.',
              },
            ].map((step, i) => (
              <div
                key={i}
                className={`hiw-step ${visible['hiw'] ? 'fade-up' : 'pre-fade'}`}
                style={{ animationDelay: `${0.1 + i * 0.12}s` }}
              >
                <div className="hiw-num">{step.n}</div>
                <div className="hiw-content">
                  <h3 className="hiw-title">{step.title}</h3>
                  <p className="hiw-desc">{step.desc}</p>
                </div>
                {i < 3 && <div className="hiw-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="cta-section">
        <div className="cta-inner container">
          <div className="cta-eyebrow">Ready to start?</div>
          <h2 className="cta-heading">
            Dedicated mentorship.<br />All the way to placement.
          </h2>
          <p className="cta-sub">
            This isn't about shortcuts. It's about making sure you don't miss the
            opportunities you already have. One mentor. One goal. Your placement.
          </p>
          <a
            href="https://pages.razorpay.com/pl_RxYpGKIFTyqm4e/view"
            className="btn-cta"
            target="_blank"
            rel="noreferrer"
          >
            Enroll in Personal Mentorship — ₹7,999
          </a>
          <p className="cta-disclaimer">
            Not a placement program. A preparation one — until the job is yours.
          </p>
        </div>
      </section>

      <style jsx>{`
        /* ─── Disclaimer Banner ─── */
        .disclaimer-banner {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
          background: #fffbeb;
          border: 1px solid #fcd34d;
          border-left: 4px solid #f59e0b;
          border-radius: 0.75rem;
          padding: 1.25rem 1.5rem;
          margin-bottom: 2.5rem;
        }
        .disclaimer-icon { font-size: 1.1rem; flex-shrink: 0; margin-top: 0.1rem; }
        .disclaimer-banner p {
          font-family: 'system-ui', sans-serif;
          font-size: 0.875rem;
          line-height: 1.65;
          color: #92400e;
          margin: 0;
        }
        .disclaimer-banner strong { color: #78350f; }

        /* ─── Plan commitment lines ─── */
        .plan-commitment {
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          margin: 0.5rem 0 1.25rem;
        }
        .commitment-line {
          display: flex;
          gap: 0.6rem;
          align-items: flex-start;
          font-family: 'system-ui', sans-serif;
          font-size: 0.8rem;
          line-height: 1.5;
          color: rgba(255,255,255,0.8);
        }
        .commitment-check {
          color: var(--secondary, #16a34a);
          font-weight: 700;
          flex-shrink: 0;
        }

        /* ─── ROI Section ─── */
        .roi-section { background: var(--surface, #f8fafc); }

        .roi-card {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }

        .roi-eyebrow {
          font-family: 'system-ui', sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--secondary, #16a34a);
          margin-bottom: 0.75rem;
        }

        .roi-heading {
          font-size: clamp(1.6rem, 3vw, 2.2rem);
          font-weight: 700;
          color: var(--primary, #1a2332);
          line-height: 1.25;
          margin: 0 0 1.5rem;
        }

        .roi-body {
          font-family: 'system-ui', sans-serif;
          font-size: 0.95rem;
          line-height: 1.75;
          color: var(--text-secondary, #4b5563);
          margin: 0 0 1rem;
        }

        .roi-compare {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
          border-radius: 1rem;
          overflow: hidden;
          border: 1px solid var(--border, #e5e7eb);
          box-shadow: 0 4px 24px rgba(0,0,0,0.07);
        }

        .roi-col {
          padding: 1.75rem 1.5rem;
        }
        .roi-col-low {
          background: white;
          border-right: 1px solid var(--border, #e5e7eb);
        }
        .roi-col-high {
          background: var(--primary, #1a2332);
          position: relative;
        }

        .roi-col-label {
          font-family: 'system-ui', sans-serif;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--text-secondary, #9ca3af);
          margin-bottom: 0.5rem;
        }

        .roi-col-badge {
          display: inline-block;
          font-family: 'system-ui', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--secondary, #16a34a);
          background: rgba(22,163,74,0.15);
          padding: 0.2rem 0.6rem;
          border-radius: 100px;
          margin-bottom: 0.5rem;
        }

        .roi-col-amount {
          font-size: 2rem;
          font-weight: 700;
          color: #9ca3af;
          line-height: 1;
          margin-bottom: 0.5rem;
          text-decoration: line-through;
        }
        .roi-col-amount-high {
          color: white;
          text-decoration: none;
        }

        .roi-col-note {
          font-family: 'system-ui', sans-serif;
          font-size: 0.75rem;
          color: var(--text-secondary, #9ca3af);
          margin-bottom: 1rem;
          line-height: 1.4;
        }
        .roi-col-high .roi-col-note {
          color: rgba(255,255,255,0.5);
        }

        .roi-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
        }
        .roi-list li {
          font-family: 'system-ui', sans-serif;
          font-size: 0.78rem;
          color: var(--text-secondary, #6b7280);
          line-height: 1.45;
          padding-left: 0.9rem;
          position: relative;
        }
        .roi-list li::before {
          content: '–';
          position: absolute;
          left: 0;
          color: #d1d5db;
        }
        .roi-col-high .roi-list li {
          color: rgba(255,255,255,0.65);
        }
        .roi-col-high .roi-list li::before {
          color: var(--secondary, #16a34a);
          content: '✓';
        }

        .roi-diff {
          margin-top: 1.25rem;
          background: var(--secondary, #16a34a);
          color: white;
          text-align: center;
          padding: 0.5rem;
          border-radius: 0.4rem;
          font-family: 'system-ui', sans-serif;
          font-size: 0.82rem;
          font-weight: 700;
        }

        /* ─── CTA disclaimer ─── */
        .cta-disclaimer {
          font-family: 'system-ui', sans-serif;
          font-size: 0.75rem;
          color: rgba(255,255,255,0.35);
          margin-top: 1rem;
        }

        /* ─── Base ─── */
        .mentorship-page {
          font-family: 'Georgia', 'Times New Roman', serif;
          color: var(--text, #1a2332);
          background: var(--bg, #ffffff);
          overflow-x: hidden;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-gap {
          padding: 7rem 0;
        }

        /* ─── Animations ─── */
        .pre-fade {
          opacity: 0;
          transform: translateY(28px);
        }
        .fade-up {
          animation: fadeUp 0.65s ease forwards;
        }
        .delay-1 { animation-delay: 0.1s; }
        .delay-2 { animation-delay: 0.2s; }
        .delay-3 { animation-delay: 0.3s; }

        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }

        /* ─── Section Labels ─── */
        .section-label {
          font-family: 'system-ui', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--secondary, #16a34a);
          margin-bottom: 0.75rem;
        }

        .section-heading {
          font-size: clamp(2rem, 4vw, 2.8rem);
          font-weight: 700;
          color: var(--primary, #1a2332);
          margin: 0 0 0.5rem;
          line-height: 1.2;
        }

        .section-sub {
          font-family: 'system-ui', sans-serif;
          font-size: 1.05rem;
          color: var(--text-secondary, #6b7280);
          margin: 0 0 3.5rem;
        }

        /* ─── HERO ─── */
        .hero-section {
          position: relative;
          min-height: 90vh;
          display: flex;
          align-items: center;
          background: var(--surface, #f8fafc);
          overflow: hidden;
        }

        .hero-bg-pattern {
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(circle at 80% 20%, rgba(22,163,74,0.06) 0%, transparent 50%),
            radial-gradient(circle at 10% 80%, rgba(22,163,74,0.04) 0%, transparent 40%);
        }

        .hero-inner {
          position: relative;
          z-index: 1;
          padding-top: 5rem;
          padding-bottom: 5rem;
          max-width: 820px;
        }

        .hero-badge {
          display: inline-block;
          font-family: 'system-ui', sans-serif;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--secondary, #16a34a);
          background: rgba(22,163,74,0.1);
          padding: 0.35rem 0.9rem;
          border-radius: 100px;
          margin-bottom: 2rem;
        }

        .hero-title {
          font-size: clamp(2.6rem, 6vw, 4.2rem);
          font-weight: 700;
          line-height: 1.15;
          color: var(--primary, #1a2332);
          margin: 0 0 1.5rem;
        }

        .hero-accent {
          color: var(--secondary, #16a34a);
        }

        .hero-sub {
          font-family: 'system-ui', sans-serif;
          font-size: 1.15rem;
          line-height: 1.75;
          color: var(--text-secondary, #4b5563);
          max-width: 600px;
          margin: 0 0 2.5rem;
        }

        .hero-cta-group {
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .btn-primary-hero {
          font-family: 'system-ui', sans-serif;
          background: var(--primary, #1a2332);
          color: white;
          text-decoration: none;
          padding: 0.9rem 2rem;
          border-radius: 0.5rem;
          font-weight: 600;
          font-size: 0.95rem;
          transition: background 0.2s, transform 0.2s;
          display: inline-block;
        }
        .btn-primary-hero:hover {
          background: var(--secondary, #16a34a);
          transform: translateY(-1px);
        }

        .btn-ghost-hero {
          font-family: 'system-ui', sans-serif;
          color: var(--text-secondary, #6b7280);
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
          transition: color 0.2s;
        }
        .btn-ghost-hero:hover { color: var(--primary, #1a2332); }

        .hero-scroll-line {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--border, #e5e7eb);
        }

        /* ─── PLAN CARD ─── */
        .plan-card {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 0;
          border: 1px solid var(--border, #e5e7eb);
          border-radius: 1.25rem;
          overflow: hidden;
          background: white;
          box-shadow: 0 4px 32px rgba(0,0,0,0.06);
        }

        .plan-left {
          background: var(--primary, #1a2332);
          color: white;
          padding: 2.5rem 2rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .plan-tag {
          font-family: 'system-ui', sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.5);
        }

        .plan-price-block {
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
          margin-top: 0.25rem;
        }

        .plan-price {
          font-size: 2.8rem;
          font-weight: 700;
          line-height: 1;
        }

        .plan-price-note {
          font-family: 'system-ui', sans-serif;
          font-size: 0.8rem;
          color: rgba(255,255,255,0.5);
        }

        .plan-pitch {
          font-family: 'system-ui', sans-serif;
          font-size: 0.9rem;
          line-height: 1.65;
          color: rgba(255,255,255,0.75);
          margin: 0.5rem 0 1rem;
          flex: 1;
        }

        .btn-plan {
          display: block;
          text-align: center;
          background: var(--secondary, #16a34a);
          color: white;
          text-decoration: none;
          padding: 0.85rem 1.5rem;
          border-radius: 0.5rem;
          font-family: 'system-ui', sans-serif;
          font-weight: 700;
          font-size: 0.9rem;
          transition: background 0.2s, transform 0.2s;
        }
        .btn-plan:hover {
          background: #15803d;
          transform: translateY(-1px);
        }

        .plan-note {
          font-family: 'system-ui', sans-serif;
          font-size: 0.72rem;
          color: rgba(255,255,255,0.4);
          text-align: center;
          margin: 0;
        }

        .plan-features {
          padding: 2rem 2.5rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0;
        }

        .feature-item {
          display: flex;
          gap: 0.9rem;
          align-items: flex-start;
          padding: 1.25rem 1rem;
          border-bottom: 1px solid var(--border, #f3f4f6);
          border-right: 1px solid var(--border, #f3f4f6);
          transition: background 0.15s;
        }
        .feature-item:hover { background: rgba(22,163,74,0.03); }

        /* Remove right border for 2nd column items */
        .feature-item:nth-child(even) { border-right: none; }
        /* Remove bottom border for last two items */
        .feature-item:nth-last-child(-n+2) { border-bottom: none; }

        .feature-icon {
          font-size: 1.3rem;
          line-height: 1;
          margin-top: 0.1rem;
          flex-shrink: 0;
        }

        .feature-title {
          font-family: 'system-ui', sans-serif;
          font-size: 0.88rem;
          font-weight: 700;
          color: var(--primary, #1a2332);
          margin-bottom: 0.3rem;
        }

        .feature-desc {
          font-family: 'system-ui', sans-serif;
          font-size: 0.8rem;
          line-height: 1.55;
          color: var(--text-secondary, #6b7280);
        }

        /* ─── MENTOR ─── */
        .mentor-section {
          background: var(--surface, #f8fafc);
        }

        .mentor-card {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 4rem;
          align-items: start;
        }

        .mentor-photo-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .mentor-photo-frame {
          width: 180px;
          height: 180px;
          border-radius: 50%;
          overflow: hidden;
          border: 3px solid var(--secondary, #16a34a);
          box-shadow: 0 8px 24px rgba(22,163,74,0.15);
        }

        .mentor-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .mentor-linkedin {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-family: 'system-ui', sans-serif;
          font-size: 0.78rem;
          font-weight: 600;
          color: #0a66c2;
          text-decoration: none;
          padding: 0.4rem 0.9rem;
          border: 1px solid #0a66c2;
          border-radius: 100px;
          transition: background 0.2s, color 0.2s;
        }
        .mentor-linkedin:hover {
          background: #0a66c2;
          color: white;
        }

        .mentor-tags-col {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          justify-content: center;
        }

        .mentor-tag {
          font-family: 'system-ui', sans-serif;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--primary, #1a2332);
          background: white;
          border: 1px solid var(--border, #e5e7eb);
          padding: 0.25rem 0.65rem;
          border-radius: 100px;
        }

        .mentor-bio {
          padding: 1rem 0;
        }

        .mentor-quote {
          font-size: 1.25rem;
          font-style: italic;
          color: var(--primary, #1a2332);
          border-left: 3px solid var(--secondary, #16a34a);
          padding-left: 1.25rem;
          margin-bottom: 1.75rem;
          line-height: 1.5;
        }

        .mentor-bio p {
          font-family: 'system-ui', sans-serif;
          font-size: 0.95rem;
          line-height: 1.75;
          color: var(--text-secondary, #4b5563);
          margin: 0 0 1rem;
        }

        .mentor-stats {
          display: flex;
          align-items: center;
          gap: 2rem;
          margin-top: 2rem;
          padding: 1.5rem 2rem;
          background: white;
          border-radius: 0.75rem;
          border: 1px solid var(--border, #e5e7eb);
        }

        .stat {
          text-align: center;
          flex: 1;
        }

        .stat-number {
          font-size: 1.8rem;
          font-weight: 700;
          color: var(--primary, #1a2332);
          line-height: 1;
        }

        .stat-label {
          font-family: 'system-ui', sans-serif;
          font-size: 0.75rem;
          color: var(--text-secondary, #9ca3af);
          margin-top: 0.3rem;
        }

        .stat-divider {
          width: 1px;
          height: 40px;
          background: var(--border, #e5e7eb);
          flex-shrink: 0;
        }

        /* ─── HOW IT WORKS ─── */
        .hiw-section {
          background: white;
        }

        .hiw-steps {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0;
          position: relative;
        }

        .hiw-step {
          position: relative;
          padding: 2rem 1.5rem;
          background: var(--surface, #f8fafc);
          border: 1px solid var(--border, #e5e7eb);
          border-right: none;
          transition: box-shadow 0.2s;
        }
        .hiw-step:first-child { border-radius: 0.75rem 0 0 0.75rem; }
        .hiw-step:last-child {
          border-right: 1px solid var(--border, #e5e7eb);
          border-radius: 0 0.75rem 0.75rem 0;
        }
        .hiw-step:hover {
          background: white;
          box-shadow: 0 4px 20px rgba(0,0,0,0.07);
          z-index: 1;
        }

        .hiw-num {
          font-size: 2.5rem;
          font-weight: 700;
          color: rgba(22,163,74,0.15);
          line-height: 1;
          margin-bottom: 1rem;
        }

        .hiw-title {
          font-family: 'system-ui', sans-serif;
          font-size: 1rem;
          font-weight: 700;
          color: var(--primary, #1a2332);
          margin: 0 0 0.6rem;
        }

        .hiw-desc {
          font-family: 'system-ui', sans-serif;
          font-size: 0.82rem;
          line-height: 1.65;
          color: var(--text-secondary, #6b7280);
          margin: 0;
        }

        /* ─── CTA ─── */
        .cta-section {
          background: var(--primary, #1a2332);
          padding: 7rem 0;
          text-align: center;
        }

        .cta-eyebrow {
          font-family: 'system-ui', sans-serif;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--secondary, #16a34a);
          margin-bottom: 1.25rem;
        }

        .cta-heading {
          font-size: clamp(2.2rem, 5vw, 3.5rem);
          font-weight: 700;
          color: white;
          margin: 0 0 1.25rem;
          line-height: 1.2;
        }

        .cta-sub {
          font-family: 'system-ui', sans-serif;
          font-size: 1.05rem;
          line-height: 1.75;
          color: rgba(255,255,255,0.6);
          max-width: 560px;
          margin: 0 auto 2.5rem;
        }

        .btn-cta {
          display: inline-block;
          background: var(--secondary, #16a34a);
          color: white;
          text-decoration: none;
          padding: 1rem 2.25rem;
          border-radius: 0.5rem;
          font-family: 'system-ui', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          transition: background 0.2s, transform 0.2s;
        }
        .btn-cta:hover {
          background: #15803d;
          transform: translateY(-2px);
        }

        /* ─── Responsive ─── */
        @media (max-width: 900px) {
          .roi-card { grid-template-columns: 1fr; gap: 2.5rem; }
          .plan-card {
            grid-template-columns: 1fr;
          }
          .plan-features {
            grid-template-columns: 1fr;
          }
          .feature-item {
            border-right: none;
          }
          .feature-item:nth-last-child(-n+2) {
            border-bottom: 1px solid var(--border, #f3f4f6);
          }
          .feature-item:last-child {
            border-bottom: none;
          }
          .mentor-card {
            grid-template-columns: 1fr;
            gap: 2.5rem;
          }
          .mentor-photo-col { order: 0; }
          .hiw-steps {
            grid-template-columns: 1fr;
          }
          .hiw-step {
            border-right: 1px solid var(--border, #e5e7eb);
            border-bottom: none;
            border-radius: 0;
          }
          .hiw-step:first-child { border-radius: 0.75rem 0.75rem 0 0; }
          .hiw-step:last-child {
            border-bottom: 1px solid var(--border, #e5e7eb);
            border-radius: 0 0 0.75rem 0.75rem;
          }
        }

        @media (max-width: 600px) {
          .section-gap { padding: 4rem 0; }
          .plan-features { padding: 1.5rem 1.25rem; }
          .mentor-stats { flex-direction: column; gap: 1rem; }
          .stat-divider { width: 40px; height: 1px; }
          .hero-cta-group { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default Mentorship;